import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { useDateFormatter } from './useDateFormatter';
import { validate } from '../contracts/validate';
import '../contracts/Payroll_CancelEmployeeDeductionInput';
import '../contracts/Payroll_CreateEmployeeDeductionInput.ts';
import '../contracts/Payroll_UpdateEmployeeDeductionInput.ts';
import '../contracts/Payroll_ProcessDiscountsInput.ts';
import '../contracts/Payroll_ReopenInput.ts';
import '../contracts/Payroll_DeleteEmployeeDeductionResponse';
import '../contracts/Payroll_UpdateEmployeeDeductionResponse';

import '../contracts/Payroll_CancelEmployeeDeductionResponse';
import '../contracts/Payroll_CreateEmployeeDeductionResponse';
import '../contracts/Payroll_EmployeeDeductionResponse';
import '../contracts/Payroll_OpenDeductionsForPeriodResponse';
import '../contracts/Payroll_ProcessDeductionDiscountsResponse';
import '../contracts/Payroll_ReopenEmployeeDeductionResponse';
import '../contracts/Payroll_EmployeeDeductionsResponse';
import type { Payroll_CreateEmployeeDeductionInput } from '../contracts/Payroll_CreateEmployeeDeductionInput';
import type { Payroll_UpdateEmployeeDeductionInput } from '../contracts/Payroll_UpdateEmployeeDeductionInput';
import type { Payroll_ProcessDiscountsInput } from '../contracts/Payroll_ProcessDiscountsInput';
/**
 * Status constants (mirror backend)
 */
export const DEDUCTION_STATUS = {
  OPEN: 1,
  PARTIAL: 2,
  PAID: 3,
  CANCELED: 4
};

export const DEDUCTION_STATUS_LABELS = {
  1: 'Aberto',
  2: 'Parcial',
  3: 'Pago',
  4: 'Cancelado'
};

export const DEDUCTION_STATUS_SEVERITY = {
  1: 'info',      // Blue
  2: 'warning',   // Orange/Yellow
  3: 'success',   // Green
  4: 'danger'     // Red
};

/**
 * Composable for managing employee deductions
 *
 * NEW design - independent of payroll period:
 * - Deductions have status: open, partial, paid, canceled
 * - Deductions are linked to payroll via employee_deduction_items
 * - NO expense relationship (unlike advances)
 */
export function useEmployeeDeductions() {
  const isLoading = ref(false);
  const error = ref(null);

  const { fromApiDateToDate, fromDateToApiDate } = useDateFormatter();

  /**
   * Transform API response to typed data
   */
  const transformDeduction = (data: any) => {
    return {
      ...data,
      // Parse amounts
      amount: parseFloat(data.amount || 0),
      amount_paid: parseFloat(data.amount_paid || 0),
      amount_remaining: parseFloat(data.amount || 0) - parseFloat(data.amount_paid || 0),
      // Status helpers
      status_label: DEDUCTION_STATUS_LABELS[data.status as keyof typeof DEDUCTION_STATUS_LABELS] || 'Desconhecido',
      status_severity: DEDUCTION_STATUS_SEVERITY[data.status as keyof typeof DEDUCTION_STATUS_SEVERITY] || 'info'
    };
  };

  /**
   * List deductions with filters
   */
  const listDeductions = async (filters: any = {}) => {
    isLoading.value = true;
    error.value = null;

    try {
      const params = new URLSearchParams();
      if (filters.franchise_id) params.append('franchise_id', filters.franchise_id);
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.status !== undefined && filters.status !== null) params.append('status', filters.status);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.open_or_partial) params.append('open_or_partial', '1');

      const queryString = params.toString();
      const url = `/payroll/employee_deductions${queryString ? '?' + queryString : ''}`;
      log('useEmployeeDeductions', 'listDeductions', 'Fetching', url);

      const response = await httpService.get(url, { responseContract: 'Payroll_EmployeeDeductionsResponse' });

      if (response.success) {
        // The Payroll_EmployeeDeductionsResponse contract nests the array under
        // dados.deductions; fall back to a bare-array dados for resilience.
        const rawDeductions = Array.isArray(response.dados)
          ? response.dados
          : Array.isArray(response.dados?.deductions)
            ? response.dados.deductions
            : [];
        log('useEmployeeDeductions', 'listDeductions', 'Success', { count: rawDeductions.length });
        return rawDeductions.map(transformDeduction);
      } else {
        throw new Error(response.error || 'Falha ao buscar deduções');
      }
    } catch (err) {
      error.value = err.message;
      log('useEmployeeDeductions', 'listDeductions', 'Error', err);
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get single deduction by ID
   */
  const getDeduction = async (id: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/payroll/employee_deduction/${id}`, { responseContract: 'Payroll_EmployeeDeductionResponse' });

      if (response.success && response.dados) {
        return transformDeduction(response.dados);
      } else {
        throw new Error(response.error || 'Dedução não encontrada');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create a new deduction
   */
  const createDeduction = async (data: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Payroll_CreateEmployeeDeductionInput = {
        employee_id: data.employee_id,
        franchise_id: data.franchise_id,
        title: data.title,
        description: data.description || undefined,
        amount: data.amount,
        deduction_date: data.deduction_date
      };

      log('useEmployeeDeductions', 'createDeduction', 'Creating', payload);

      validate('Payroll_CreateEmployeeDeductionInput', payload);
      const response = await httpService.post('/payroll/create_employee_deduction', payload, { responseContract: 'Payroll_CreateEmployeeDeductionResponse' });

      if (response.success) {
        return transformDeduction(response.dados);
      } else {
        throw new Error(response.error || 'Falha ao criar dedução');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update an existing deduction (only if open)
   */
  const updateDeduction = async (id: any, data: Payroll_UpdateEmployeeDeductionInput) => {
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Payroll_UpdateEmployeeDeductionInput = {};
      if (data.title !== undefined) payload.title = data.title;
      if (data.description !== undefined) payload.description = data.description;
      if (data.amount !== undefined) payload.amount = data.amount;
      if (data.deduction_date !== undefined) payload.deduction_date = data.deduction_date;

      validate('Payroll_UpdateEmployeeDeductionInput', payload);
      const response = await httpService.put(`/payroll/update_employee_deduction/${id}`, payload, {
        responseContract: 'Payroll_UpdateEmployeeDeductionResponse',
        inputContract: 'Payroll_UpdateEmployeeDeductionInput',
      });

      if (response.success) {
        return transformDeduction(response.dados);
      } else {
        throw new Error(response.error || 'Falha ao atualizar dedução');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete a deduction (only if open)
   */
  const deleteDeduction = async (id: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.delete(`/payroll/delete_employee_deduction/${id}`, {
        responseContract: 'Payroll_DeleteEmployeeDeductionResponse',
      });
      return response.success;
    } catch (err) {
      error.value = err.message;
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Cancel a deduction
   */
  const cancelDeduction = async (id: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/payroll/cancel_employee_deduction/${id}`, null, { inputContract: 'Payroll_CancelEmployeeDeductionInput', responseContract: 'Payroll_CancelEmployeeDeductionResponse' });

      if (response.success) {
        return transformDeduction(response.dados);
      } else {
        throw new Error(response.error || 'Falha ao cancelar dedução');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Reopen a deduction (from canceled or paid status)
   */
  const reopenDeduction = async (id: number | string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/payroll/reopen_employee_deduction/${id}`, null, { inputContract: 'Payroll_ReopenInput', responseContract: 'Payroll_ReopenEmployeeDeductionResponse' });

      if (response.success) {
        return transformDeduction(response.dados);
      } else {
        throw new Error(response.error || 'Falha ao reabrir dedução');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get open/partial deductions for a payroll period (wizard)
   */
  const getOpenDeductionsForPeriod = async (periodId: number) => {
    isLoading.value = true;
    error.value = null;

    log('useEmployeeDeductions', 'getOpenDeductionsForPeriod', '📡 START - Calling API', {
      periodId: periodId,
      endpoint: `/payroll/open_deductions_for_period/${periodId}`
    });

    try {
      const response = await httpService.get(`/payroll/open_deductions_for_period/${periodId}`, { responseContract: 'Payroll_OpenDeductionsForPeriodResponse' });

      log('useEmployeeDeductions', 'getOpenDeductionsForPeriod', '📦 Raw API response', {
        success: response.success,
        hasData: !!response.dados,
        rawDeductionsCount: response.dados?.deductions?.length || 0,
        rawDeductionIds: response.dados?.deductions?.map((d: any) => d.id) || [],
        rawTotalOpen: response.dados?.total_open,
        rawSelectedItemsCount: response.dados?.selected_items?.length || 0
      });

      if (response.success && response.dados) {
        const data = response.dados;
        const transformedDeductions = (data.deductions || []).map(transformDeduction);

        const result = {
          deductions: transformedDeductions,
          summary_by_employee: data.summary_by_employee || [],
          total_open: data.total_open || 0,
          // NEW: Include already-selected items for this period (for resuming/editing)
          selected_items: data.selected_items || []
        };

        log('useEmployeeDeductions', 'getOpenDeductionsForPeriod', '✅ END - Returning transformed data', {
          deductionsCount: result.deductions.length,
          deductionDetails: result.deductions.map((d: any) => ({
            id: d.id,
            idType: typeof d.id,
            title: d.title,
            amount_remaining: d.amount_remaining,
            status: d.status
          })),
          totalOpen: result.total_open,
          summaryByEmployeeCount: result.summary_by_employee.length,
          selectedItemsCount: result.selected_items.length,
          selectedItemsDetails: result.selected_items.map((item: any) => ({
            deduction_id: item.deduction_id,
            amount: item.amount,
            title: item.title
          }))
        });

        return result;
      } else {
        throw new Error(response.error || 'Falha ao buscar deduções abertas');
      }
    } catch (err) {
      error.value = err.message;
      log('useEmployeeDeductions', 'getOpenDeductionsForPeriod', '❌ Error', err);
      return { deductions: [], summary_by_employee: [], total_open: 0, selected_items: [] };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Process deduction discounts (wizard confirmation)
   */
  const processDiscounts = async (periodId: any, discounts: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Payroll_ProcessDiscountsInput = {
        period_id: periodId,
        discounts: discounts.map((d: any) => ({
          deduction_id: d.deduction_id,
          amount: d.amount
        }))
      };

      validate('Payroll_ProcessDiscountsInput', payload);
      const response = await httpService.post('/payroll/process_deduction_discounts', payload, { responseContract: 'Payroll_ProcessDeductionDiscountsResponse' });

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Falha ao processar descontos');
      }
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Constants
    DEDUCTION_STATUS,
    DEDUCTION_STATUS_LABELS,
    DEDUCTION_STATUS_SEVERITY,

    // Methods
    listDeductions,
    getDeduction,
    createDeduction,
    updateDeduction,
    deleteDeduction,
    cancelDeduction,
    reopenDeduction,
    getOpenDeductionsForPeriod,
    processDiscounts
  };
}
